import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, RefreshCw, Search, ChevronLeft, Mail, Send, FileText, Loader2,
  Globe, Save, ShieldAlert, X, Edit3, Star, Download, Ban, Sparkles,
  History as HistoryIcon, Archive as ArchiveIcon, RotateCcw, Trash2, AlertTriangle,
  Inbox, CheckCircle2, XCircle, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminMembersList, adminMemberDossier, adminMemberPatchNotes,
  adminMemberSendEmail, adminMemberMessages, adminMemberAudit,
  adminNewsletterQueue, adminNewsletterList, adminNewsletterPreview,
  adminNewsletterConfirmSend,
  adminMemberPatch, adminMemberRegenerateCallsign, adminMemberExport,
  adminMemberDesignations, adminMemberSendDossierUpdate,
  adminMemberReservedCallsigns, adminRbacMe,
  adminMemberArchive, adminMemberRestore, adminMemberDelete,
  adminMemberEmailHistory, adminEmailRetry,
} from "../../lib/api";
import { EmailDeliveryBanner, EmailHealthPanel } from "./EmailHealthPanel";
import { NetworkBadge, ConfidenceBadge } from "./NetworkBadges";
import {
  isFoundingMember, FOUNDING_GOLD, FOUNDING_GOLD_DIM,
  FOUNDING_GOLD_GLOW_BG, FoundingBadge, foundingCallsignColor,
  foundingStarsStyle,
} from "../founding";
import { emitArchiveInvalidate, useArchiveInvalidate } from "../../lib/archive-events";

/**
 * AdminAssetDirectory — admin view of all members.
 *
 * Two surfaces:
 *   • Asset Directory (list + filter + search)
 *   • Newsletter Queue (queue → preview → confirm send)
 *
 * Per-member dossier drawer reuses the visitor-dossier visual language.
 * Individual email + newsletter delivery is currently STUB only (status
 * "queued" / "sent_stub") — actual Resend wiring ships in Phase B.
 */

const TIER_META = {
  subscriber: { label: "SUBSCRIBER", color: "#9FA8D4" },
  member:     { label: "MEMBER",     color: "#5DD4B3" },
};

export const AdminAssetDirectory = ({ token, userToken = null }) => {
  const [tab, setTab] = useState("directory"); // directory | newsletter
  return (
    <div data-testid="admin-asset-directory">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Users size={13} /> // ASSET DIRECTORY
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">
            Members
          </h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Long-term identity layer. Every member has a persistent callsign,
            engagement record, and admin-only dossier. Privacy first — no
            public member list. Newsletter and individual emails are queued
            and audit-logged.
          </p>
        </div>
      </div>

      <div className="flex border-b border-[#2D2A26] mb-3">
        {[
          ["directory", "Directory"],
          ["newsletter", "Newsletter Queue"],
        ].map(([k, l]) => {
          const active = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`ad-tab-${k}`}
              className={
                "px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active
                  ? "border-[#D92D20] text-[#D92D20]"
                  : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >
              {l}
            </button>
          );
        })}
      </div>

      {tab === "directory" && <DirectoryView token={token} userToken={userToken} />}
      {tab === "newsletter" && <NewsletterView token={token} userToken={userToken} />}
    </div>
  );
};

// ---------------------------------------------------------------------
// Directory list + per-member drawer
// ---------------------------------------------------------------------

const DirectoryView = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tier, setTier] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  const [counts, setCounts] = useState({ active: 0, archived: 0, all: 0 });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (tier !== "all") params.tier = tier;
      if (search.trim()) params.search = search.trim();
      const r = await adminMembersList(token, params, userToken);
      setRows(r.rows || []);
      if (r.counts) setCounts(r.counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [token, userToken, tier, statusFilter, search]);

  useEffect(() => { load(); }, [load]);
  useArchiveInvalidate("members", load);

  // Open a specific member dossier via URL hash (e.g. coming from
  // MemberDirectory): #member=<id>
  useEffect(() => {
    const parse = () => {
      const h = window.location.hash || "";
      const m = h.match(/member=([^&]+)/);
      if (m && m[1]) setSelected(m[1]);
    };
    parse();
    window.addEventListener("hashchange", parse);
    return () => window.removeEventListener("hashchange", parse);
  }, []);

  return (
    <div>
      <div className="mb-3">
        <EmailDeliveryBanner token={token} userToken={userToken} />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {/* Status filter — Active / Archived / All. Active hides archived rows by default. */}
        <div className="flex gap-1" data-testid="ad-status-pills">
          {[
            { key: "active",   label: "Active",   testid: "ad-status-active",   count: counts.active },
            { key: "archived", label: "Archived", testid: "ad-status-archived", count: counts.archived },
            { key: "all",      label: "All",      testid: "ad-status-all",      count: counts.all },
          ].map((opt) => {
            const active = statusFilter === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setStatusFilter(opt.key)}
                data-testid={opt.testid}
                className={
                  "px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border inline-flex items-center gap-1.5 " +
                  (active
                    ? (opt.key === "archived"
                        ? "border-[#E0A526] text-[#E0A526] bg-[#1a1408]"
                        : "border-[#D92D20] text-[#D92D20] bg-[#1a0a0a]")
                    : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")
                }
              >
                {opt.key === "archived" && <ArchiveIcon size={10} />}
                {opt.label}
                <span className="opacity-70">· {opt.count}</span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-1">
          {["all", "subscriber", "member"].map((t) => {
            const active = tier === t;
            return (
              <button
                key={t}
                onClick={() => setTier(t)}
                data-testid={`ad-tier-${t}`}
                className={
                  "px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border " +
                  (active
                    ? "border-[#D92D20] text-[#D92D20] bg-[#1a0a0a]"
                    : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")
                }
              >
                {t}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a544a]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search callsign or email…"
            data-testid="ad-search"
            className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none pl-7 pr-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
          />
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="ad-refresh"
          className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="border border-[#2D2A26] bg-[#131313]" data-testid="ad-list">
        {loading && rows.length === 0 ? (
          <div className="px-3 py-6 font-mono text-xs text-[#757065]">// LOADING…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs uppercase tracking-widest">
            No members yet.
          </div>
        ) : rows.map((r) => {
          const isFounding = isFoundingMember(r);
          const tm = isFounding
            ? { label: "FOUNDING", color: FOUNDING_GOLD }
            : (TIER_META[r.tier] || TIER_META.subscriber);
          const stars = Math.max(0, Math.min(5, Number(r.clearance_level || 0)));
          return (
            <button
              key={r.id}
              onClick={() => setSelected(r.id)}
              data-testid={`ad-row-${r.id}`}
              className="flex flex-wrap items-center gap-3 w-full text-left border-b px-3 py-2 hover:bg-[#0a0907] transition-colors"
              style={isFounding
                ? {
                    borderBottomColor: FOUNDING_GOLD_DIM,
                    borderLeft: `2px solid ${FOUNDING_GOLD_DIM}`,
                    backgroundColor: FOUNDING_GOLD_GLOW_BG,
                    paddingLeft: "0.75rem",
                  }
                : { borderBottomColor: "#2D2A26" }}
            >
              <span
                style={{
                  color: tm.color,
                  borderColor: tm.color,
                  backgroundColor: isFounding ? FOUNDING_GOLD_GLOW_BG : undefined,
                }}
                className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5 shrink-0 inline-flex items-center gap-1"
              >
                {isFounding && <Sparkles size={9} />} {tm.label}
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className="font-mono text-sm truncate"
                  style={{ color: foundingCallsignColor(isFounding) }}
                  data-testid={`ad-row-callsign-${r.id}`}
                >
                  {r.callsign || "—"}
                </div>
                <div className="font-mono text-[10px] text-[#757065] truncate">
                  {r.email} · {r.country_cc || "??"} · follows {r.followed_count}
                  {r.designation && r.designation !== "Subscriber" && !isFounding ? ` · ${r.designation}` : ""}
                </div>
              </div>
              {stars > 0 && (
                <span
                  className="font-mono text-[10px] hidden sm:inline tracking-wider"
                  style={foundingStarsStyle(isFounding)}
                  data-testid={`ad-row-clearance-${r.id}`}
                  title={`Clearance level ${stars}/5`}
                >
                  {"★".repeat(stars)}{"☆".repeat(5 - stars)}
                </span>
              )}
              <span className="font-mono text-[9px] text-[#757065] hidden sm:inline">
                joined {(r.joined_at || "").slice(0, 10)}
              </span>
              <span
                className="font-mono text-[9px] uppercase tracking-[0.25em]"
                style={{ color: isFounding ? FOUNDING_GOLD : "#D92D20" }}
              >
                Open dossier →
              </span>
            </button>
          );
        })}
      </div>

      {selected && (
        <MemberDossierDrawer
          token={token}
          userToken={userToken}
          memberId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Per-member dossier drawer
// ---------------------------------------------------------------------

const MemberDossierDrawer = ({ token, userToken, memberId, onClose }) => {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showDossierUpdate, setShowDossierUpdate] = useState(false);
  const [messages, setMessages] = useState([]);

  const exportRecord = useCallback(async (mid) => {
    try {
      const r = await adminMemberExport(token, mid, userToken);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `archive-member-${mid}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Record exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  }, [token, userToken]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminMemberDossier(token, memberId, userToken);
      setM(r.member);
      setNotes(r.member.admin_notes || "");
      const mr = await adminMemberMessages(token, memberId, userToken);
      setMessages(mr.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dossier");
    } finally {
      setLoading(false);
    }
  }, [token, userToken, memberId]);

  useEffect(() => { reload(); }, [reload]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await adminMemberPatchNotes(token, memberId, notes, userToken);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-end bg-black/70 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      data-testid="ad-dossier-drawer"
    >
      <div className="w-full max-w-xl h-full bg-[#0a0907] border-l border-[#2D2A26] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[#2D2A26] bg-[#0a0907] px-3 py-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="ad-dossier-close"
            className="inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // MEMBER DOSSIER
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]"
            aria-label="Close"
          >
            <X size={13} />
          </button>
        </div>

        {loading || !m ? (
          <div className="p-6 font-mono text-xs text-[#757065]">// LOADING…</div>
        ) : (
          <div className="p-4 space-y-3">
            <IdentityHeadline m={m} />
            <IdentityBlock m={m} token={token} userToken={userToken} onChanged={reload} />

            <DossierBlock title="Status & Clearance">
              <StatusClearanceEditor m={m} token={token} userToken={userToken} onChanged={reload} />
            </DossierBlock>

            <DossierBlock title="One-click">
              <QuickActions m={m} token={token} userToken={userToken} onChanged={reload} />
            </DossierBlock>

            <DossierBlock title="Engagement (90d)">
              <div className="grid grid-cols-2 gap-3 font-mono text-[10px]">
                <Stat label="Page views" value={m.engagement?.page_views_30d ?? "—"} />
                <Stat label="Heartbeats" value={m.engagement?.heartbeats_30d ?? "—"} />
                <Stat label="Dossiers viewed" value={m.dossiers_viewed_90d ?? "—"} />
                <Stat label="Sources opened" value={m.sources_opened_90d ?? "—"} />
              </div>
            </DossierBlock>

            <DossierBlock title="Geo">
              <div className="font-mono text-[10px] text-[#A39E93]">
                Country: {m.country_cc || "—"}
                {m.region ? ` · Region: ${m.region}` : ""}
                {m.city ? ` · City: ${m.city}` : ""}
              </div>
            </DossierBlock>

            <DossierBlock title="Followed">
              <div className="font-mono text-[10px] text-[#A39E93]">
                Topics: {(m.followed_topics || []).join(", ") || "—"}
              </div>
              <div className="font-mono text-[10px] text-[#A39E93] mt-1">
                Epstein: {(m.followed_ef_entities || []).join(", ") || "—"}
              </div>
            </DossierBlock>

            <DossierBlock title="Achievements">
              <div className="flex flex-wrap gap-1.5">
                {(m.achievements || []).map((a) => (
                  <span
                    key={a}
                    className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5"
                  >
                    {a}
                  </span>
                ))}
                {(m.achievements || []).length === 0 && (
                  <span className="font-mono text-[10px] text-[#757065]">none</span>
                )}
              </div>
            </DossierBlock>

            <DossierBlock title="Newsletter prefs">
              <div className="font-mono text-[10px] text-[#A39E93] flex flex-wrap gap-2">
                <span>weekly_brief: {m.newsletter_prefs?.weekly_brief ? "✓" : "—"}</span>
                <span>major_releases: {m.newsletter_prefs?.major_releases ? "✓" : "—"}</span>
              </div>
            </DossierBlock>

            <DossierBlock title="Segment tags">
              <SegmentTagsEditor m={m} token={token} userToken={userToken} onChanged={reload} />
            </DossierBlock>

            <DossierBlock title="Admin notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={4000}
                rows={4}
                data-testid="ad-dossier-notes"
                className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3]"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={saveNotes}
                  disabled={savingNotes}
                  data-testid="ad-dossier-notes-save"
                  className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
                >
                  {savingNotes ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save notes
                </button>
              </div>
            </DossierBlock>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ad-dossier-actions">
              <button
                type="button"
                onClick={() => setShowSend(true)}
                data-testid="ad-dossier-send-email"
                className="inline-flex items-center gap-1.5 border border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
              >
                <Mail size={11} /> Send Email
              </button>
              <button
                type="button"
                onClick={() => setShowDossierUpdate(true)}
                data-testid="ad-dossier-send-dossier-update"
                className="inline-flex items-center gap-1.5 border border-[#9FA8D4] text-[#9FA8D4] hover:bg-[#0a0d1a] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
              >
                <FileText size={11} /> Send Dossier Update
              </button>
              <SuppressToggle m={m} token={token} userToken={userToken} onChanged={reload} />
              <button
                type="button"
                onClick={() => exportRecord(m.id)}
                data-testid="ad-dossier-export"
                className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
              >
                <Download size={11} /> Export Record
              </button>
            </div>

            <DossierBlock title={`Email history · ${messages.length}`}>
              {messages.length === 0 ? (
                <div className="font-mono text-[10px] text-[#757065]">No messages sent.</div>
              ) : (
                <div className="space-y-1.5">
                  {messages.slice(0, 12).map((mm) => (
                    <div key={mm.id} className="border border-[#2D2A26] p-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] flex items-center gap-2">
                        <span className="text-[#E0A526]">{(mm.status || "queued").toUpperCase()}</span>
                        <span>·</span>
                        <span>{(mm.created_at || "").slice(0, 16)}</span>
                        {mm.delivery === "stub" && (
                          <span className="ml-auto text-[#E0A526]">STUB · NOT SENT</span>
                        )}
                      </div>
                      <div className="text-sm text-[#E4DFD3] truncate">{mm.subject}</div>
                    </div>
                  ))}
                </div>
              )}
            </DossierBlock>

            <DossierBlock title="Email Delivery">
              <MemberEmailHistory memberId={memberId} token={token} userToken={userToken} />
            </DossierBlock>

            <DossierBlock title="Audit history">
              <MemberAuditHistory memberId={memberId} token={token} userToken={userToken} />
            </DossierBlock>

            <DangerZone
              m={m}
              token={token}
              userToken={userToken}
              onArchived={reload}
              onDeleted={() => { onClose(); }}
            />
          </div>
        )}

        {showSend && (
          <SendEmailModal
            token={token}
            userToken={userToken}
            memberId={memberId}
            recipientEmail={m?.email}
            initialSubject=""
            initialBody=""
            onClose={() => setShowSend(false)}
            onSent={() => { setShowSend(false); reload(); }}
          />
        )}
        {showDossierUpdate && (
          <SendDossierUpdateModal
            token={token}
            userToken={userToken}
            memberId={memberId}
            recipientEmail={m?.email}
            onClose={() => setShowDossierUpdate(false)}
            onSent={() => { setShowDossierUpdate(false); reload(); }}
          />
        )}
      </div>
    </div>
  );
};

const DossierBlock = ({ title, children }) => (
  <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2">
      {title}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[#757065] uppercase tracking-[0.25em]">{label}</div>
    <div className="text-[#E4DFD3]">{value}</div>
  </div>
);

// ---------------------------------------------------------------------
// Send-individual-email modal
// ---------------------------------------------------------------------

const SendEmailModal = ({ token, userToken, memberId, recipientEmail, initialSubject = "", initialBody = "", onClose, onSent }) => {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("compose"); // compose | preview

  const queueIt = async () => {
    setBusy(true);
    try {
      await adminMemberSendEmail(token, memberId, subject, body, userToken);
      toast.success("Queued — delivery stubbed (Phase B will wire Resend)");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Queue failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ad-send-email-modal"
    >
      <div className="w-full max-w-xl border border-[#5DD4B3] bg-[#0a0907]">
        <div className="flex items-center gap-2 border-b border-[#5DD4B3] px-4 py-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3]">
            // send individual email · {stage}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#E4DFD3]"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <EmailDeliveryBanner token={token} userToken={userToken} />

          {stage === "compose" && (
            <>
              <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="ad-send-subject"
                className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
              />
              <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                data-testid="ad-send-body"
                className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setStage("preview")}
                  disabled={!subject.trim() || !body.trim()}
                  data-testid="ad-send-preview"
                  className="bg-[#454038] hover:bg-[#5a544a] text-[#E4DFD3] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
                >
                  Preview →
                </button>
              </div>
            </>
          )}

          {stage === "preview" && (
            <>
              <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">
                  subject
                </div>
                <div className="font-mono text-sm text-[#E4DFD3] mb-3">{subject}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">
                  body
                </div>
                <pre className="whitespace-pre-wrap font-mono text-[12px] text-[#CBB892]">{body}</pre>
              </div>
              <div className="flex justify-between gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setStage("compose")}
                  disabled={busy}
                  className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
                >
                  ← Back to compose
                </button>
                <button
                  type="button"
                  onClick={queueIt}
                  disabled={busy}
                  data-testid="ad-send-confirm"
                  className="inline-flex items-center gap-1.5 bg-[#5DD4B3] hover:bg-[#3FB996] text-[#0F0F0F] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
                >
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                  Queue + audit
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Newsletter view — queue / preview / confirm pipeline
// ---------------------------------------------------------------------

const NewsletterView = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewSample, setPreviewSample] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminNewsletterList(token, userToken);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const startPreview = async (nl) => {
    setBusy(true);
    setPreviewing(nl);
    try {
      const r = await adminNewsletterPreview(token, nl.id, userToken);
      setPreviewSample(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreviewing(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmSend = async () => {
    if (!previewing) return;
    setBusy(true);
    try {
      await adminNewsletterConfirmSend(token, previewing.id, userToken);
      toast.success("Confirmed — STUB delivery recorded (Phase B will deliver via Resend)");
      setPreviewing(null);
      setPreviewSample(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setShowCompose(true)}
          data-testid="ad-nl-compose-btn"
          className="inline-flex items-center gap-1.5 border border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        >
          <FileText size={11} /> Compose newsletter
        </button>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mb-4">
        <EmailHealthPanel token={token} userToken={userToken} />
      </div>

      <div className="border border-[#2D2A26] bg-[#131313]" data-testid="ad-nl-list">
        {rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs uppercase tracking-widest">
            No newsletters queued.
          </div>
        ) : rows.map((nl) => (
          <div
            key={nl.id}
            data-testid={`ad-nl-row-${nl.id}`}
            className="flex flex-wrap items-center gap-3 border-b border-[#2D2A26] px-3 py-2"
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">
              {(nl.status || "queued").toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#E4DFD3] truncate">{nl.subject}</div>
              <div className="font-mono text-[10px] text-[#757065]">
                {nl.audience} · {nl.recipient_count ?? 0} recipients · {(nl.created_at || "").slice(0, 16)}
              </div>
            </div>
            {(nl.status === "queued" || nl.status === "previewed") && (
              <button
                type="button"
                onClick={() => startPreview(nl)}
                data-testid={`ad-nl-preview-${nl.id}`}
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/60 px-2 py-1 hover:bg-[#0c1a0e]"
              >
                Preview → Confirm
              </button>
            )}
          </div>
        ))}
      </div>

      {showCompose && (
        <ComposeNewsletter
          token={token}
          userToken={userToken}
          onClose={() => setShowCompose(false)}
          onQueued={() => { setShowCompose(false); load(); }}
        />
      )}

      {previewing && (
        <PreviewNewsletter
          newsletter={previewing}
          sample={previewSample}
          busy={busy}
          token={token}
          userToken={userToken}
          onClose={() => { setPreviewing(null); setPreviewSample(null); }}
          onConfirm={confirmSend}
        />
      )}
    </div>
  );
};

const ComposeNewsletter = ({ token, userToken, onClose, onQueued }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("weekly_brief");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminNewsletterQueue(token, { subject, body, audience }, userToken);
      toast.success("Newsletter queued");
      onQueued();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Queue failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ad-nl-compose-modal"
    >
      <div className="w-full max-w-2xl border border-[#5DD4B3] bg-[#0a0907]">
        <div className="flex items-center gap-2 border-b border-[#5DD4B3] px-4 py-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3]">
            // newsletter · compose
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#E4DFD3]"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Audience</label>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            data-testid="ad-nl-audience"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
          >
            <option value="weekly_brief">weekly_brief (members with weekly_brief = true)</option>
            <option value="major_releases">major_releases (members with major_releases = true)</option>
            <option value="all_subscribers">all_subscribers</option>
          </select>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="ad-nl-subject"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
          />
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            data-testid="ad-nl-body"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !subject.trim() || !body.trim()}
              data-testid="ad-nl-queue-confirm"
              className="inline-flex items-center gap-1.5 bg-[#5DD4B3] hover:bg-[#3FB996] text-[#0F0F0F] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : null}
              Queue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewNewsletter = ({ newsletter, sample, busy, onClose, onConfirm, token, userToken }) => (
  <div
    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
    role="dialog"
    aria-modal="true"
    data-testid="ad-nl-preview-modal"
  >
    <div className="w-full max-w-2xl border border-[#E0A526] bg-[#0a0907]">
      <div className="flex items-center gap-2 border-b border-[#E0A526] px-4 py-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526]">
          // newsletter · preview · confirm send
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#E4DFD3]"
        >
          <X size={12} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <EmailDeliveryBanner token={token} userToken={userToken} />
        <div className="font-mono text-[10px] text-[#A39E93]">
          audience: {newsletter.audience} · recipients: {newsletter.recipient_count ?? 0}
        </div>
        <div className="font-mono text-sm text-[#E4DFD3]">{newsletter.subject}</div>
        <pre className="whitespace-pre-wrap font-mono text-[12px] text-[#CBB892] border border-[#2D2A26] bg-[#0F0F0F] p-2 max-h-[40vh] overflow-auto">
{newsletter.body}
        </pre>
        {sample?.sample_recipients && (
          <div className="font-mono text-[10px] text-[#A39E93]">
            sample recipients: {(sample.sample_recipients || []).map((r) => r.callsign || "—").join(", ")}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="ad-nl-confirm-send"
            className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Confirm + send (stub)
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------
// Identity block — Callsign / Email / Designation / Member Since
// ---------------------------------------------------------------------

const IdentityBlock = ({ m, token, userToken, onChanged }) => {
  const [editingCallsign, setEditingCallsign] = useState(false);
  const [cs, setCs] = useState(m.callsign || "");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [reservedNames, setReservedNames] = useState([]);
  const [designations, setDesignations] = useState([
    "Founding Member", "Intelligence Contributor", "Senior Researcher",
    "Trusted Source", "Field Analyst", "Archivist", "Operations Support",
    "Editorial Advisor", "Verified Research Partner", "Beta Tester",
    "Community Investigator", "Subject Matter Expert", "Honorary Member",
  ]);

  useEffect(() => {
    adminMemberDesignations(token, userToken)
      .then((r) => r?.designations && setDesignations(r.designations))
      .catch(() => {});
    // Owner-gate the Reserved Callsigns picker. Backend remains the
    // authority on assignment; this just decides whether to render the UI.
    adminRbacMe(token, userToken)
      .then((r) => setIsOwner(!!r?.is_owner))
      .catch(() => setIsOwner(false));
    adminMemberReservedCallsigns(token, userToken)
      .then((r) => setReservedNames(r?.reserved || []))
      .catch(() => setReservedNames([]));
  }, [token, userToken]);

  const pickReserved = (name) => {
    setCs(name);
    setOverride(true);
  };

  const saveCallsign = async () => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id,
        { callsign: cs, override_reserved: override }, userToken);
      toast.success("Callsign updated");
      setEditingCallsign(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate callsign? The current callsign will be replaced.")) return;
    setBusy(true);
    try {
      const r = await adminMemberRegenerateCallsign(token, m.id, userToken);
      toast.success(`Callsign → ${r.new_callsign}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  const resetAuto = async () => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, { callsign_overridden: false }, userToken);
      toast.success("Callsign reset to automatic");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const changeDesignation = async (d) => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, { designation: d }, userToken);
      toast.success(`Designation → ${d}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ad-dossier-identity"
      className="border p-3 transition-colors"
      style={isFoundingMember(m)
        ? {
            borderColor: FOUNDING_GOLD_DIM,
            backgroundColor: FOUNDING_GOLD_GLOW_BG,
          }
        : { borderColor: "#2D2A26", backgroundColor: "#0F0F0F" }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
          identity
        </div>
        {isFoundingMember(m) && <FoundingBadge size="sm" testid="ad-identity-founding-badge" />}
      </div>
      <div className="flex flex-wrap items-baseline gap-3 mb-2">
        {!editingCallsign ? (
          <>
            <div
              data-testid="ad-dossier-callsign"
              className="font-display text-2xl"
              style={{ color: foundingCallsignColor(isFoundingMember(m)) }}
            >
              {m.callsign || "—"}
            </div>
            {m.callsign_overridden && (
              <span
                data-testid="ad-dossier-callsign-pinned"
                className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-1.5 py-0.5"
                title="Manually pinned"
              >
                PINNED
              </span>
            )}
            <button
              type="button"
              onClick={() => { setCs(m.callsign || ""); setEditingCallsign(true); }}
              data-testid="ad-dossier-callsign-edit"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20]"
            >
              <Edit3 size={11} /> Edit
            </button>
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              data-testid="ad-dossier-callsign-regen"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9FA8D4] hover:text-[#E4DFD3]"
            >
              <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Regenerate
            </button>
            {m.callsign_overridden && (
              <button
                type="button"
                onClick={resetAuto}
                disabled={busy}
                data-testid="ad-dossier-callsign-reset"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3]"
              >
                Reset to auto
              </button>
            )}
          </>
        ) : (
          <div className="w-full">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={cs}
                onChange={(e) => setCs(e.target.value.toUpperCase())}
                data-testid="ad-dossier-callsign-input"
                maxLength={24}
                placeholder="NIGHTFALL"
                className="bg-[#0a0907] border border-[#D92D20] focus:border-[#5DD4B3] outline-none px-2 py-1.5 font-mono text-sm text-[#E4DFD3] tracking-widest"
              />
              <label className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93]">
                <input
                  type="checkbox"
                  checked={override}
                  onChange={(e) => setOverride(e.target.checked)}
                  data-testid="ad-dossier-callsign-override"
                /> Override reserved
              </label>
              <button
                type="button"
                onClick={saveCallsign}
                disabled={busy}
                data-testid="ad-dossier-callsign-save"
                className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
              </button>
              <button
                type="button"
                onClick={() => setEditingCallsign(false)}
                disabled={busy}
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#E4DFD3]"
              >
                Cancel
              </button>
            </div>
            <div className="mt-1 font-mono text-[9px] text-[#5a544a]">
              // 2-24 chars · alphanumeric · hyphen / underscore allowed
            </div>

            {isOwner && reservedNames.length > 0 && (
              <div className="mt-2 border border-[#E0A526]/30 bg-[#1a1408] p-2" data-testid="ad-reserved-callsigns">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] mb-1.5 inline-flex items-center gap-1.5">
                  <Sparkles size={10} /> RESERVED CALLSIGNS · OWNER-ONLY
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {reservedNames.map((name) => {
                    const selected = cs === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => pickReserved(name)}
                        disabled={busy}
                        data-testid={`ad-reserved-pick-${name}`}
                        className={
                          "inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.25em] border px-2 py-1 transition-colors disabled:opacity-50 " +
                          (selected
                            ? "border-[#E0A526] bg-[#2a1a08] text-[#E0A526]"
                            : "border-[#454038] text-[#CBB892] hover:border-[#E0A526] hover:text-[#E0A526]")
                        }
                      >
                        {name}
                        <span className="text-[8px] tracking-[0.3em] text-[#E0A526]/70 ml-1">
                          RES.
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1.5 font-mono text-[8.5px] text-[#5a544a] tracking-[0.25em]">
                  // click to fill · confirm with Save · backend still enforces uniqueness
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="font-mono text-[10px] text-[#A39E93] flex items-center gap-2 flex-wrap">
        <span>{(m.tier || "subscriber").toUpperCase()}</span>
        <span>·</span>
        <span data-testid="ad-dossier-email">{m.email}</span>
        {m.country_cc && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Globe size={10} /> {m.country_cc}
            </span>
          </>
        )}
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-2 font-mono text-[10px]">
        <div>
          <div className="text-[#757065] uppercase tracking-[0.25em]">designation</div>
          <select
            value={m.designation || "Subscriber"}
            onChange={(e) => changeDesignation(e.target.value)}
            disabled={busy}
            data-testid="ad-dossier-designation-select"
            className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 text-[#E4DFD3]"
          >
            <option value="Subscriber">Subscriber</option>
            {designations.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="text-[#757065] uppercase tracking-[0.25em]">member since · last active</div>
          <div className="text-[#E4DFD3]">
            {(m.joined_at || "").slice(0, 10)} · {(m.last_seen_at || "").slice(0, 10)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// MemberEmailHistory — delivery roll-up + retry. Lives inside the dossier.
// ---------------------------------------------------------------------
const MEMBER_STATUS_META = {
  queued:               { label: "QUEUED",         color: "#A39E93" },
  queued_disabled:      { label: "DELIVERY OFF",   color: "#E0A526" },
  sending:              { label: "SENDING",        color: "#9FA8D4" },
  delivered:            { label: "DELIVERED",      color: "#5DD4B3" },
  "delivered.confirmed": { label: "CONFIRMED",     color: "#5DD4B3" },
  opened:               { label: "OPENED",         color: "#5DD4B3" },
  clicked:              { label: "CLICKED",        color: "#5DD4B3" },
  failed:               { label: "FAILED",         color: "#D92D20" },
  bounced:              { label: "BOUNCED",        color: "#D92D20" },
  suppressed:           { label: "SUPPRESSED",     color: "#E0A526" },
  retry_pending:        { label: "RETRY PENDING",  color: "#E0A526" },
};

const MemberEmailHistory = ({ memberId, token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminMemberEmailHistory(token, memberId, userToken);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load email history");
    } finally {
      setLoading(false);
    }
  }, [token, userToken, memberId]);

  useEffect(() => { load(); }, [load]);

  const retry = async (messageId) => {
    setRetryingId(messageId);
    try {
      const r = await adminEmailRetry(token, messageId, userToken);
      toast.success(`Retry: ${r.status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  if (loading || !data) {
    return <div className="font-mono text-[10px] text-[#757065]">// Loading email history…</div>;
  }
  return (
    <div data-testid="member-email-history">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Pill label="Emails Sent"           value={data.emails_sent ?? 0}        color={data.emails_sent ? "#5DD4B3" : "#A39E93"} testid="meh-emails-sent" />
        <Pill label="Newsletters Received"  value={data.newsletters_received ?? 0} color={data.newsletters_received ? "#5DD4B3" : "#A39E93"} testid="meh-newsletters" />
        <Pill label="Welcome Email"         value={data.welcome_email ? "✓" : "—"} color={data.welcome_email ? "#5DD4B3" : "#A39E93"} testid="meh-welcome" />
        <Pill label="Failures"              value={data.failures ?? 0}            color={data.failures ? "#D92D20" : "#A39E93"} testid="meh-failures" />
      </div>
      {data.last_delivery && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-2 mb-3" data-testid="meh-last-delivery">
          <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#A39E93]">Last delivery</div>
          <div className="font-mono text-[11px] text-[#E4DFD3] truncate mt-0.5">
            {data.last_delivery.subject || "(no subject)"}
          </div>
          <div className="font-mono text-[10px] text-[#5a544a]">
            {(data.last_delivery.delivered_at || data.last_delivery.failed_at || "").slice(0, 16).replace("T", " ")}
            {" · "}
            <span style={{ color: MEMBER_STATUS_META[data.last_delivery.status]?.color || "#A39E93" }}>
              {MEMBER_STATUS_META[data.last_delivery.status]?.label || data.last_delivery.status}
            </span>
          </div>
        </div>
      )}
      {data.messages && data.messages.length > 0 ? (
        <div className="space-y-1">
          {data.messages.slice(0, 8).map((m) => {
            const meta = MEMBER_STATUS_META[m.status] || { label: m.status, color: "#A39E93" };
            const retryable = ["failed", "queued_disabled", "retry_pending"].includes(m.status);
            const attempts = m.attempt_count || 0;
            return (
              <div
                key={m.id}
                data-testid={`meh-row-${m.id}`}
                className="border border-[#2D2A26] p-2 flex items-center gap-2"
              >
                <span
                  style={{ color: meta.color, borderColor: meta.color }}
                  className="font-mono text-[8.5px] uppercase tracking-[0.25em] border px-1.5 py-0.5 shrink-0"
                  data-testid={`meh-status-${m.id}`}
                >
                  {meta.label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] text-[#E4DFD3] truncate">{m.subject || "(no subject)"}</div>
                  <div className="font-mono text-[9px] text-[#5a544a] truncate">
                    {m.kind} · {(m.created_at || "").slice(0, 16).replace("T", " ")}
                    {attempts > 1 && ` · ${attempts} attempts`}
                    {m.last_error && <span className="text-[#E0A526]"> · {m.last_error.slice(0, 80)}</span>}
                  </div>
                </div>
                {retryable && (
                  <button
                    type="button"
                    onClick={() => retry(m.id)}
                    disabled={retryingId === m.id}
                    data-testid={`meh-retry-${m.id}`}
                    className="inline-flex items-center gap-1 border border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] disabled:opacity-50"
                  >
                    {retryingId === m.id ? <Loader2 size={9} className="animate-spin" /> : <RefreshCcw size={9} />}
                    Retry
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="font-mono text-[10px] text-[#757065]">// No emails sent to this member yet.</div>
      )}
    </div>
  );
};

const Pill = ({ label, value, color, testid }) => (
  <div
    data-testid={testid}
    className="border p-2"
    style={{ borderColor: `${color}55` }}
  >
    <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#A39E93]">{label}</div>
    <div className="font-display text-xl mt-0.5" style={{ color }}>{value}</div>
  </div>
);

// ---------------------------------------------------------------------
// MemberAuditHistory — surfaces admin_audit_log entries that target this
// member. Lives inside the dossier so admins can see every change to
// this account in one place.
// ---------------------------------------------------------------------
const MemberAuditHistory = ({ memberId, token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminMemberAudit(token, memberId, 50, userToken)
      .then((r) => { if (!cancelled) setRows(r.rows || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [memberId, token, userToken]);

  if (loading) {
    return <div className="font-mono text-[10px] text-[#757065]">// loading…</div>;
  }
  if (rows.length === 0) {
    return <div className="font-mono text-[10px] text-[#757065]">No audit entries for this asset.</div>;
  }
  return (
    <div className="space-y-1" data-testid="ad-dossier-audit-history">
      {rows.slice(0, 20).map((r, i) => {
        const changes = (r.metadata && r.metadata.changes) || null;
        return (
          <div key={i} className="border-l-2 border-[#2D2A26] pl-2 py-1" data-testid={`ad-dossier-audit-row-${i}`}>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] flex flex-wrap items-baseline gap-2">
              <span className="text-[#D92D20]">{r.event}</span>
              <span className="text-[#A39E93]">{r.actor_email || "operator-token"}</span>
              <span className="ml-auto text-[#5a544a] tracking-normal text-[9px]">
                {(r.ts || "").slice(0, 19).replace("T", " ")}
              </span>
            </div>
            {changes && Object.keys(changes).length > 0 && (
              <div className="mt-0.5 font-mono text-[10px] text-[#CBB892]">
                {Object.entries(changes).slice(0, 4).map(([k, v]) => (
                  <span key={k} className="mr-3">
                    {k}: <span className="text-[#757065]">{JSON.stringify(v?.[0])} → </span>
                    <span className="text-[#E4DFD3]">{JSON.stringify(v?.[1])}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


// ---------------------------------------------------------------------
// Danger Zone — Archive (soft delete) / Restore / Delete Permanently.
// Archive + Restore are available to any admin. Delete is Owner-only and
// guarded by a typed "DELETE" confirmation + required reason. Backend
// enforces every constraint independently of this UI.
// ---------------------------------------------------------------------
const DangerZone = ({ m, token, userToken, onArchived, onDeleted }) => {
  const [isOwner, setIsOwner] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const isArchived = (m.status || "active") === "archived";

  useEffect(() => {
    adminRbacMe(token, userToken)
      .then((r) => setIsOwner(!!r?.is_owner))
      .catch(() => setIsOwner(false));
  }, [token, userToken]);

  const handleArchive = async () => {
    const reason = window.prompt(
      `Archive ${m.callsign || m.email}?\n\nThe member will be hidden from active lists but all history is preserved. You can restore them later.\n\nOptional reason for the audit log:`,
      "",
    );
    if (reason === null) return; // cancelled
    setBusy(true);
    try {
      await adminMemberArchive(token, m.id, reason || null, userToken);
      toast.success("Member archived");
      onArchived?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    try {
      await adminMemberRestore(token, m.id, userToken);
      toast.success("Member restored");
      onArchived?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-[#D92D20]/40 bg-[#1a0a0a]/40 p-3 mt-4"
      data-testid="ad-danger-zone"
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={12} className="text-[#D92D20]" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          Danger Zone
        </div>
      </div>
      <p className="font-mono text-[10px] text-[#A39E93] mb-3 leading-relaxed">
        Archive removes the member from active lists but preserves all history.
        Delete is permanent and Owner-only — it cannot be undone.
      </p>
      <div className="flex flex-wrap gap-2">
        {!isArchived ? (
          <button
            type="button"
            onClick={handleArchive}
            disabled={busy}
            data-testid="ad-danger-archive"
            className="inline-flex items-center gap-1.5 border border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-50"
          >
            <ArchiveIcon size={11} /> Archive Member
          </button>
        ) : (
          <button
            type="button"
            onClick={handleRestore}
            disabled={busy}
            data-testid="ad-danger-restore"
            className="inline-flex items-center gap-1.5 border border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-50"
          >
            <RotateCcw size={11} /> Restore Member
          </button>
        )}
        {isOwner && (
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            disabled={busy}
            data-testid="ad-danger-delete"
            className="inline-flex items-center gap-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-50"
          >
            <Trash2 size={11} /> Delete Permanently
          </button>
        )}
        {!isOwner && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#5a544a]"
            data-testid="ad-danger-owner-only-hint"
          >
            <ShieldAlert size={11} /> Permanent delete requires Owner
          </span>
        )}
      </div>

      {isArchived && (m.archive_reason || m.archived_at) && (
        <div className="mt-3 border-t border-[#2D2A26] pt-2 font-mono text-[10px] text-[#A39E93]">
          Archived {(m.archived_at || "").slice(0, 16).replace("T", " ")}
          {m.archive_reason && (
            <span className="block mt-1 text-[#CBB892]">
              Reason: {m.archive_reason}
            </span>
          )}
        </div>
      )}

      {showDeleteModal && (
        <DeleteMemberModal
          m={m}
          token={token}
          userToken={userToken}
          onClose={() => setShowDeleteModal(false)}
          onDeleted={() => { setShowDeleteModal(false); onDeleted?.(); }}
        />
      )}
    </div>
  );
};

const DeleteMemberModal = ({ m, token, userToken, onClose, onDeleted }) => {
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [releaseCallsign, setReleaseCallsign] = useState(true);
  const [busy, setBusy] = useState(false);

  const canSubmit = confirm.trim() === "DELETE" && reason.trim().length >= 4 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await adminMemberDelete(token, m.id, {
        confirm: "DELETE",
        reason: reason.trim(),
        release_callsign: releaseCallsign,
      }, userToken);
      toast.success(`Deleted ${m.callsign || m.email}`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ad-delete-modal"
    >
      <div className="w-full max-w-md bg-[#0a0907] border border-[#D92D20] p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={14} className="text-[#D92D20]" />
          <h3 className="font-display text-xl text-[#E4DFD3]">Delete Member Permanently</h3>
        </div>
        <div className="font-mono text-[11px] text-[#A39E93] mb-4 leading-relaxed">
          You are about to permanently delete{" "}
          <span className="text-[#E4DFD3]">{m.callsign || "—"}</span>
          {" "}<span className="text-[#757065]">({m.email})</span>.
          This cannot be undone. The member row, active sessions, and email
          history will be removed. Audit log and subscription ledger entries
          are preserved as a tombstone.
        </div>
        <label className="block mb-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
            Reason (required, audit-logged)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            data-testid="ad-delete-reason"
            placeholder="e.g. test account, GDPR request, spam signup…"
            className="mt-1 w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
          />
        </label>
        <label className="block mb-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">
            Type <span className="text-[#D92D20]">DELETE</span> to confirm
          </span>
          <input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="ad-delete-confirm"
            placeholder="DELETE"
            autoComplete="off"
            className="mt-1 w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-sm text-[#E4DFD3] tracking-widest"
          />
        </label>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={releaseCallsign}
            onChange={(e) => setReleaseCallsign(e.target.checked)}
            data-testid="ad-delete-release-callsign"
            className="accent-[#D92D20]"
          />
          <span className="font-mono text-[11px] text-[#A39E93]">
            Release callsign back to the available pool (skipped for reserved honorifics)
          </span>
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="ad-delete-cancel"
            className="border border-[#454038] hover:border-[#E4DFD3] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="ad-delete-submit"
            className="inline-flex items-center gap-1.5 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] disabled:opacity-40"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------
// Identity headline — CALLSIGN / DESIGNATION / CLEARANCE / STATUS / MEMBER SINCE
// ---------------------------------------------------------------------
const HeadlineField = ({ label, value, color, testid }) => (
  <div className="border border-[#2D2A26] bg-[#0d0c0a] px-3 py-2" data-testid={testid}>
    <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065]">
      {label}
    </div>
    <div
      className="font-display text-base mt-0.5 truncate"
      style={{ color: color || "#E4DFD3" }}
    >
      {value}
    </div>
  </div>
);

const IdentityHeadline = ({ m }) => {
  const isFounding = isFoundingMember(m);
  const stars = Math.max(0, Math.min(5, Number(m.clearance_level || 0)));
  const status = (m.status || "active").toUpperCase();
  const statusColor = status === "ACTIVE" ? "#5DD4B3" : status === "SUSPENDED" ? "#E0A526" : "#D92D20";
  const joinedMonth = (() => {
    if (!m.joined_at) return "—";
    try {
      const d = new Date(m.joined_at);
      return d.toLocaleString("en-US", { month: "long", year: "numeric" });
    } catch {
      return (m.joined_at || "").slice(0, 7);
    }
  })();

  return (
    <div
      data-testid="ad-dossier-headline"
      className="border p-4 transition-colors"
      style={isFounding
        ? {
            borderColor: FOUNDING_GOLD_DIM,
            backgroundColor: FOUNDING_GOLD_GLOW_BG,
            boxShadow: "inset 0 0 0 1px rgba(224, 165, 38, 0.10)",
          }
        : { borderColor: "#2D2A26", backgroundColor: "#0F0F0F" }}
    >
      {isFounding && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <FoundingBadge size="md" withGrantLine testid="ad-headline-founding-badge" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
            Honorary Tier
          </span>
        </div>
      )}

      {/* Primary identity: CALLSIGN is the title — email is secondary */}
      <div className="mb-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">
          Callsign
        </div>
        <h2
          className="font-display text-3xl sm:text-4xl tracking-tight leading-none"
          style={{ color: foundingCallsignColor(isFounding) }}
          data-testid="ad-headline-callsign"
        >
          {m.callsign || "—"}
        </h2>
        {m.email && (
          <div
            className="mt-2 font-mono text-[11px] text-[#A39E93] truncate"
            data-testid="ad-headline-email"
          >
            {m.email}
          </div>
        )}
      </div>

      {/* Metadata: Designation · Status · Member Since · Last Login · Clearance */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <HeadlineField
          label="Designation"
          value={(m.designation || "Subscriber").toUpperCase()}
          color={isFounding ? FOUNDING_GOLD : "#CBB892"}
          testid="ad-headline-designation"
        />
        <HeadlineField
          label="Status"
          value={status}
          color={statusColor}
          testid="ad-headline-status"
        />
        <HeadlineField
          label="Member Since"
          value={joinedMonth}
          color={foundingCallsignColor(isFounding)}
          testid="ad-headline-member-since"
        />
        <HeadlineField
          label="Last Login"
          value={(() => {
            const ll = m.last_login_at || m.last_seen_at;
            if (!ll) return "—";
            try {
              const d = new Date(ll);
              const now = Date.now();
              const diffH = (now - d.getTime()) / 3.6e6;
              if (diffH < 1) return "just now";
              if (diffH < 24) return `${Math.floor(diffH)}h ago`;
              const diffD = Math.floor(diffH / 24);
              if (diffD < 30) return `${diffD}d ago`;
              return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
            } catch {
              return (ll || "").slice(0, 10);
            }
          })()}
          color="#A39E93"
          testid="ad-headline-last-login"
        />
        <div
          className="border px-3 py-2"
          data-testid="ad-headline-clearance"
          style={isFounding
            ? { borderColor: FOUNDING_GOLD_DIM, backgroundColor: "rgba(0,0,0,0.20)" }
            : { borderColor: "#2D2A26", backgroundColor: "#0d0c0a" }}
        >
          <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065]">
            Clearance
          </div>
          <div
            className="font-mono text-base mt-0.5 tracking-wider"
            style={foundingStarsStyle(isFounding)}
          >
            {stars > 0 ? "★".repeat(stars) + "☆".repeat(5 - stars) : "—"}
          </div>
        </div>
      </div>

      {(m.network_type || m.location_confidence) && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ad-headline-network-row">
          {m.network_type && <NetworkBadge type={m.network_type} small testid="ad-headline-network" />}
          {m.location_confidence && <ConfidenceBadge level={m.location_confidence} small testid="ad-headline-confidence" />}
          <span className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5a544a]">
            // privacy-safe · no IPs retained
          </span>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------
// One-click quick actions: Promote to Member, Mark Founding Member,
// Reset to Subscriber. Founding Member assignment requires Owner — the
// backend will reject non-Owner calls with 403 and we surface the
// resulting toast.
// ---------------------------------------------------------------------
const QuickActions = ({ m, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const tier = (m.tier || "subscriber").toLowerCase();
  const isFounding = (m.designation || "").toLowerCase() === "founding member";
  const isSuspended = (m.status || "active").toLowerCase() === "suspended";

  const run = async (body, msg) => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, body, userToken);
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="ad-dossier-quick-actions">
      {tier !== "member" && (
        <button
          type="button"
          onClick={() => run({ tier: "member" }, "Promoted to MEMBER")}
          disabled={busy}
          data-testid="ad-quick-promote-member"
          className="inline-flex items-center gap-1.5 border border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          <Sparkles size={11} /> Promote to Member
        </button>
      )}
      {!isFounding && (
        <button
          type="button"
          onClick={() => run({ designation: "Founding Member", tier: "member" }, "Marked Founding Member")}
          disabled={busy}
          data-testid="ad-quick-mark-founding"
          className="inline-flex items-center gap-1.5 border border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
          title="Owner-only — backend will reject if you are not the Owner"
        >
          <Sparkles size={11} /> Mark Founding Member
        </button>
      )}
      {isFounding && (
        <button
          type="button"
          onClick={() => run({ designation: "" }, "Revoked Founding Member")}
          disabled={busy}
          data-testid="ad-quick-revoke-founding"
          className="inline-flex items-center gap-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
          title="Owner-only — backend will reject if you are not the Owner"
        >
          <Ban size={11} /> Revoke Founding
        </button>
      )}
      {tier === "member" && (
        <button
          type="button"
          onClick={() => run({ tier: "subscriber", designation: "" }, "Demoted to Subscriber")}
          disabled={busy}
          data-testid="ad-quick-demote-subscriber"
          className="inline-flex items-center gap-1.5 border border-[#454038] text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          Demote to Subscriber
        </button>
      )}
      <button
        type="button"
        onClick={() => run(
          { status: isSuspended ? "active" : "suspended" },
          isSuspended ? "Restored to ACTIVE" : "Suspended"
        )}
        disabled={busy}
        data-testid="ad-quick-suspend-toggle"
        className={
          "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border disabled:opacity-50 " +
          (isSuspended
            ? "border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e]"
            : "border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408]")
        }
      >
        {isSuspended ? "Restore" : "Suspend"}
      </button>
    </div>
  );
};


// ---------------------------------------------------------------------
// Segment tags editor — admin-only freeform tagging for newsletter targeting
// ---------------------------------------------------------------------
const SegmentTagsEditor = ({ m, token, userToken, onChanged }) => {
  const [tags, setTags] = useState(m.segment_tags || []);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setTags(m.segment_tags || []); }, [m.segment_tags]);

  const save = async (next) => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, { segment_tags: next }, userToken);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const addTag = () => {
    const t = draft.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
    if (!t || tags.includes(t)) { setDraft(""); return; }
    const next = [...tags, t].slice(0, 24);
    setTags(next);
    setDraft("");
    save(next);
  };

  const removeTag = (t) => {
    const next = tags.filter((x) => x !== t);
    setTags(next);
    save(next);
  };

  return (
    <div data-testid="ad-dossier-segment-tags">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.length === 0 && (
          <span className="font-mono text-[10px] text-[#757065]">// no tags yet</span>
        )}
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-2 py-0.5"
            data-testid={`ad-dossier-segment-tag-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              disabled={busy}
              className="ml-1 text-[#A39E93] hover:text-[#D92D20]"
              aria-label={`Remove ${t}`}
            >
              <X size={9} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          maxLength={32}
          placeholder="e.g. early-supporter, fed-watcher"
          data-testid="ad-dossier-segment-tag-input"
          className="flex-1 bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#9FA8D4] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3]"
        />
        <button
          type="button"
          onClick={addTag}
          disabled={busy || !draft.trim()}
          data-testid="ad-dossier-segment-tag-add"
          className="inline-flex items-center gap-1.5 border border-[#9FA8D4] text-[#9FA8D4] hover:bg-[#0a0d1a] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
        >
          Add
        </button>
      </div>
      <div className="mt-1 font-mono text-[8.5px] text-[#5a544a] uppercase tracking-[0.25em]">
        // lowercase · alphanumeric · hyphen / underscore · max 24 tags
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------
// Status & Clearance editor
// ---------------------------------------------------------------------

const StatusClearanceEditor = ({ m, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, body, userToken);
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };
  const clearance = Number(m.clearance_level || 1);
  const status = m.status || "active";

  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">clearance level</div>
        <div className="flex items-center gap-1" data-testid="ad-dossier-clearance">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => patch({ clearance_level: n }, `Clearance → ${n}★`)}
              disabled={busy}
              data-testid={`ad-dossier-clearance-${n}`}
              className="p-1"
            >
              <Star
                size={16}
                fill={n <= clearance ? "#E0A526" : "transparent"}
                color={n <= clearance ? "#E0A526" : "#454038"}
              />
            </button>
          ))}
          <span className="ml-2 font-mono text-[10px] text-[#A39E93]">{clearance} / 5</span>
        </div>
      </div>
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">status</div>
        <div className="inline-flex border border-[#2D2A26]" data-testid="ad-dossier-status">
          {["active", "suspended", "archived"].map((s) => {
            const active = status === s;
            const color = s === "active" ? "#5DD4B3" : s === "suspended" ? "#E0A526" : "#D92D20";
            return (
              <button
                key={s}
                type="button"
                onClick={() => patch({ status: s }, `Status → ${s}`)}
                disabled={busy}
                data-testid={`ad-dossier-status-${s}`}
                style={active ? { color, borderColor: color } : undefined}
                className={
                  "px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] border-r border-[#2D2A26] last:border-r-0 " +
                  (active ? "bg-[#0a0907]" : "text-[#A39E93] hover:text-[#E4DFD3]")
                }
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Suppress toggle (Unsubscribe)
// ---------------------------------------------------------------------

const SuppressToggle = ({ m, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    const next = !m.suppressed;
    if (next && !window.confirm("Suppress this subscriber? They will be removed from ALL newsletter channels until you un-suppress.")) return;
    setBusy(true);
    try {
      await adminMemberPatch(token, m.id, { suppressed: next }, userToken);
      toast.success(next ? "Suppressed" : "Un-suppressed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="ad-dossier-suppress"
      className={
        "inline-flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] " +
        (m.suppressed
          ? "border-[#E0A526] text-[#E0A526] hover:bg-[#1a1408]"
          : "border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a]")
      }
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Ban size={11} />}
      {m.suppressed ? "Un-suppress" : "Suppress / Unsubscribe"}
    </button>
  );
};

// ---------------------------------------------------------------------
// Send Dossier Update modal
// ---------------------------------------------------------------------

const SendDossierUpdateModal = ({ token, userToken, memberId, recipientEmail, onClose, onSent }) => {
  const [dossierType, setDossierType] = useState("ef_entity");
  const [dossierId, setDossierId] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!dossierId.trim()) return;
    setBusy(true);
    try {
      const r = await adminMemberSendDossierUpdate(token, memberId,
        { dossier_type: dossierType, dossier_id: dossierId.trim() }, userToken);
      setDraft(r.draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate draft");
    } finally {
      setBusy(false);
    }
  };

  if (draft) {
    // Hand off to the SendEmailModal with the prefilled draft.
    return (
      <SendEmailModal
        token={token}
        userToken={userToken}
        memberId={memberId}
        recipientEmail={recipientEmail}
        initialSubject={draft.subject}
        initialBody={draft.body}
        onClose={onClose}
        onSent={onSent}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ad-dossier-update-modal"
    >
      <div className="w-full max-w-xl border border-[#9FA8D4] bg-[#0a0907]">
        <div className="flex items-center gap-2 border-b border-[#9FA8D4] px-4 py-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9FA8D4]">
            // send dossier update · choose dossier
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#E4DFD3]"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="border border-[#E0A526] bg-[#1a1408] p-2 flex items-start gap-2 text-[11px] text-[#CBB892]">
            <Sparkles size={12} className="text-[#E0A526] mt-0.5 shrink-0" />
            <span>
              A draft email is auto-composed from the dossier&apos;s title, summary, and source trail.
              You can edit it before queueing. STUB delivery only — Resend wiring ships in Phase B.
            </span>
          </div>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">dossier type</label>
          <select
            value={dossierType}
            onChange={(e) => setDossierType(e.target.value)}
            data-testid="ad-dossier-update-type"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#9FA8D4] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
          >
            <option value="ef_entity">Epstein Files Entity</option>
            <option value="topic">Topic</option>
          </select>
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93]">dossier slug or id</label>
          <input
            value={dossierId}
            onChange={(e) => setDossierId(e.target.value)}
            placeholder={dossierType === "topic" ? "e.g. 2020-us-election-integrity-claims" : "e.g. zorro-ranch"}
            data-testid="ad-dossier-update-id"
            className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#9FA8D4] outline-none px-2 py-1.5 font-mono text-[12px] text-[#E4DFD3]"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="border border-[#454038] hover:border-[#A39E93] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={busy || !dossierId.trim()}
              data-testid="ad-dossier-update-generate"
              className="inline-flex items-center gap-1.5 bg-[#9FA8D4] hover:bg-[#8590C6] text-[#0F0F0F] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              Generate draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAssetDirectory;
